/** Central endpoint map — single source of truth for API paths. */
export const endpoints = {
  auth: {
    login: 'v1/app/auth/login',
    loginSendOtp: 'v1/app/auth/login-send-otp',
    loginVerifyOtp: 'v1/app/auth/login-verify-otp',
    signup: 'v1/app/auth/check-exists',
    fetchAccountType: 'v1/app/auth/fetch-account-type',
    verifyEmailOtp: 'v1/app/auth/verify-email-send-phone-otp',
    verifyPhoneSignup: 'v1/app/auth/verify-phone-signup',
    resendOtp: 'v1/app/auth/resend-otp',
    getByRole: 'v1/app/auth/get-by-id',
    setProfileInfo: 'v1/app/auth/set-profile-info',
    completeSignup: 'v1/app/auth/complete-signup-v2',
    logout: 'v1/app/auth/logout',
    refresh: 'v1/app/auth/refresh-token',
    forgotSendOtp: 'v1/app/forgot-password/send-otp',
    forgotVerifyOtp: 'v1/app/forgot-password/verify-otp',
    forgotResetPassword: 'v1/app/forgot-password/reset-password',
  },
  kyc: {
    verifyPan: 'v1/app/kyc/verify-pan',
    verifyGst: 'v1/app/kyc/verify-gst',
  },
  upload: {
    chunked: 'v1/common/upload/video-chunked-v2',
    deleteFile: 'v1/common/deleteFile',
  },
  dashboard: {
    campaignCount: 'v1/common/campaign/count',
  },
  campaigns: {
    availableSlots: 'v1/common/campaign/get-available-slots',
    step1: 'v1/common/campaign/step1',
    step2: 'v1/common/campaign/step2',
    step3: 'v1/common/campaign/step3',
    step4: 'v1/common/campaign/step4',
    relaunch: 'v1/common/campaign/relaunch',
    list: 'v1/common/campaign/list',
    overview: 'v1/common/campaign/overview',
    screens: 'v1/common/campaign/screens',
    progress: 'v1/common/campaign/progress',
    analytics: 'v1/common/campaign/analytics/detailed-stats',
    ageDistribution: 'v1/common/analytics/age-distribution',
    aiGeneratePreview: 'v1/common/campaign/ai-generate-preview',
    saveCampaign: 'v1/common/campaign/save-campaign',
    aiScreenCapacity: 'v1/common/campaign/ai-screen-capacity',
    slotHold: 'v1/common/campaign/slot-hold',
    updateContent: 'v1/common/campaign/update-content',
    statusCount: "v1/common/campaign/status-counts"
  },
  analytics: {
    platformStats: 'v1/common/analytics/platform-stats',
  },
  billboards: {
    list: 'v1/common/billboard/getall-billboards',
    detail: 'v1/common/billboard/billboard-overview',
    hierarchy: 'v1/common/hierarchy-v2',
  },
  locations: {
    cities: 'v1/common/billboard/cities',
    nearbyBillboards: 'v1/common/location/billboards',
  },
  testimonials: {
    list: 'v1/common/testimonials',
  },
  tutorials: {
    list: 'v1/app/tutorial/list',
  },
  appStatus: {
    popupConfig: 'v1/common/app-popup-config',
  },
  wallet: {
    balance: 'v1/common/finance/wallet/balance',
    transactions: 'v1/common/finance/transactions',
    transactionsCsv: 'v1/common/finance/transactions/csv',
    invoice: 'v1/common/finance/wallet/invoice/download',
    settlements: 'v1/common/finance/settlements',
    monthWise: 'v1/common/finance/wallet/month-wise',
    deposit: 'v1/common/finance/wallet/deposit',
    depositCharges: 'v1/common/finance/wallet/deposit-charges',
    verifyPayment: 'v1/common/finance/wallet/verify-payment',
    bankDetails: 'v1/common/finance/bank-details',
    bankDetailsList: 'v1/common/finance/fetch-all-bank-details',
    deleteBankDetails: 'v1/common/finance/delete-bank-details',
    withdrawalRequest: 'v1/common/finance/settlement/request',
    withdrawDeduction: 'v1/common/finance/calculate-withdraw-deduction',
    transferCharges: 'v1/common/finance/calculate-wallet-transfer-charges',
    transferQndt: 'v1/common/finance/wallet-to-wallet',
    searchUsers: 'v1/common/users/search',
  },
  profile: {
    view: 'v1/common/view-profile',
    update: 'v1/common/edit-user-profile',
    changePassword: 'v1/app/change-password',
  },
  totalAnalytics: {
    stats: 'v1/common/analytics/user-analytics',
    monthWise: 'v1/common/campaign/month-wise-analytics',
    ageDistribution: 'v1/common/analytics/age-distribution',
  },
  notifications: {
    list: 'v1/common/notifications',
    markRead: 'v1/common/notifications/read',
    markAllRead: 'v1/common/notifications/mark-all-read',
  },
  fcm: {
    store: 'v1/common/fcm-token/store',
    remove: 'v1/common/fcm-token/remove',
  },
  coupons: {
    available: 'v1/common/coupon/available',
    all: 'v1/common/coupon/all',
  },
  gallery: {
    upload: 'v1/app/gallery/upload',
    list: 'v1/app/gallery/my-media',
    delete: 'v1/app/gallery/delete',
    counts: 'v1/app/gallery/counts',
  },
  policies: {
    slugs: 'v1/common/static-content-slugs',
    content: 'v1/common/static-content',
  },
  support: {
    list: 'v1/app/support/getall-tickets',
    create: 'v1/app/support/tickets/create',
    detail: 'v1/app/support/tickets',
    messages: 'v1/app/support/tickets/messages',
  },
  clients: {
    list: 'v1/app/clients',
    create: 'v1/app/clients',
    detail: (id: string) => `v1/app/clients/${id}`,
    update: (id: string) => `v1/app/clients/${id}`,
    delete: (id: string) => `v1/app/clients/${id}`,
  },
  /** Agency sub-accounts (Team) — distinct from the `clients` CRM above. */
  team: {
    createFields: 'v1/app/team-members/create-fields',
    create: 'v1/app/team-members',
    list: 'v1/app/team-members',
    detail: (id: string) => `v1/app/team-members/${id}`,
    update: (id: string) => `v1/app/team-members/${id}`,
    delete: (id: string) => `v1/app/team-members/${id}`,
    assignClients: (id: string) => `v1/app/team-members/${id}/assign-clients`,
    unassignClients: (id: string) => `v1/app/team-members/${id}/unassign-clients`,
    unassignAllClients: (id: string) =>
      `v1/app/team-members/${id}/unassign-all-clients`,
    assignedClients: (id: string) => `v1/app/team-members/${id}/assigned-clients`,
    myAssignedClients: 'v1/app/team-members/my-assigned-clients',
  },
  wishlist: {
    list: 'v1/common/billboard/wishlist',
    toggle: 'v1/common/billboard/wishlist/toggle',
  },
  reports: {
    campaignsDropdown: 'v1/app/campaigns/dropdown',
    generateLink: 'v1/app/share-dashboard/generate-link',
    list: 'v1/app/share-dashboard/list',
    deleteLink: 'v1/app/share-dashboard/delete-link-byid',
    publicTab: (token: string, activeTab: string) =>
      `v1/app/public-share-dashboard/${token}/${activeTab}`,
    addComment: (token: string) =>
      `v1/app/public-share-dashboard/${token}/add-comments`,
  },
  /** Partner Personal & KYC registration (public). */
  partner: {
    tempRegistration: 'v1/common/partner/temp-registration',
    uploadSingle: 'v1/common/partner/upload/single',
  },
} as const;
